// Logic for the landing-page Chrome extension announcement. Kept out of the component so each rule is
// testable on its own.
//
// Live since the Web Store listing was approved and published: the banner always shows the Download
// copy and a real link to the listing. There used to be a build-time "coming soon" phase behind
// REACT_APP_EXTENSION_DOWNLOAD_ENABLED, gated because there was no store listing yet to link to and
// the Download button instead handed off to a sign-in -> resume -> Web Store flow; both the flag and
// that flow are gone now that the listing is live -- the button just links straight out.

export const DOWNLOAD_COPY = "See job freshness, fit, and company signals without leaving the posting";
export const DOWNLOAD_BUTTON = "Download the extension";

export const extensionId = () => process.env.REACT_APP_EXTENSION_ID || "";

// The live Web Store listing. Built from the pinned extension ID (REACT_APP_EXTENSION_ID) rather than
// hardcoded twice, but overridable for a non-standard listing URL.
export const storeUrl = () =>
  process.env.REACT_APP_EXTENSION_STORE_URL ||
  `https://chromewebstore.google.com/detail/gumi/${extensionId()}`;

// ── dismissal: per browser (localStorage) ────────────────────────────────────
// The landing page is seen signed out, so there is no user to key it to. Stored per browser (which is
// also how the extension itself is installed). Storage can be blocked (private mode), so every access
// is guarded: the banner still works, it just won't persist.
export const DISMISS_KEY = "gumi_extension_announcement_dismissed";

export function isDismissed() {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismiss() {
  try {
    window.localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* not persisted; the banner still closes for this page view */
  }
}

// ── "already has the extension" ───────────────────────────────────────────────
// Once someone has the extension we never show the announcement again. It is remembered in
// localStorage the first time we see it (detected below, or after they connect it), so a flaky
// detection later can't bring the banner back.
export const INSTALLED_KEY = "gumi_extension_installed";

export const isKnownInstalled = () => {
  try {
    return window.localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
};

export const markInstalled = () => {
  try {
    window.localStorage.setItem(INSTALLED_KEY, "1");
  } catch {
    /* fine: detection will find it again next time */
  }
};

// A web page can only talk to an extension that declares the page's origin in externally_connectable.
// GuMi's declares https://gumiscore.com (and, in dev builds only, http://localhost:3000). On those
// origins, in a Chromium browser, window.chrome.runtime.sendMessage exists only when the extension is
// installed and enabled, and the extension answers any message from them (the store build replies
// "bad_message" to unknown types). Anywhere else the answer would be "unreachable" whether or not it is
// installed, so we report "unknown" instead of guessing.
export const CONNECTABLE_ORIGINS = ["https://gumiscore.com", "http://localhost:3000"];

export function isChromium() {
  const brands = navigator.userAgentData && navigator.userAgentData.brands;
  if (brands) return brands.some((b) => /Chromium|Google Chrome|Microsoft Edge/.test(b.brand));
  return Boolean(window.chrome) && /Chrome\//.test(navigator.userAgent);
}

// -> "installed" | "not_installed" | "unknown"
export function detectExtension(timeoutMs = 1500) {
  return new Promise((resolve) => {
    const id = extensionId();
    if (!id || !isChromium() || !CONNECTABLE_ORIGINS.includes(window.location.origin)) {
      resolve("unknown");
      return;
    }
    const runtime = window.chrome && window.chrome.runtime;
    if (!runtime || typeof runtime.sendMessage !== "function") {
      resolve("not_installed");
      return;
    }
    let settled = false;
    const finish = (value) => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };
    const timer = setTimeout(() => finish("unknown"), timeoutMs);
    try {
      runtime.sendMessage(id, { type: "GUMI_PING" }, () => {
        clearTimeout(timer);
        // lastError set: no extension with that ID is reachable (not installed or disabled).
        finish(runtime.lastError ? "not_installed" : "installed");
      });
    } catch {
      clearTimeout(timer);
      finish("unknown");
    }
  });
}

// A same-site path only (never an absolute or protocol-relative URL), for "where to go next" values
// that travel through navigation state.
export function safeNext(next, fallback) {
  return typeof next === "string" && next.startsWith("/") && !next.startsWith("//") ? next : fallback;
}
